import { CardConfig, FilterConfig } from './types';

export * from './api-config';
export * from './text-constants';
export * from './table-config';
export * from './modal-config';

export const CUSTOMER_PROFILES_SECTION_KEY = 'customer-profiles';

export const CUSTOMER_PROFILES_CONFIG = {
  searchDebounce: 300,
  defaultModalSize: '2xl' as const,
  authRevealCode: '999999',
};

// TOAST STYLES CONFIGURATION
export const TOAST_STYLES = {
  success: {
    add: { className: '!bg-green-600 !text-white !border-green-700' },
    update: { className: '!bg-green-600 !text-white !border-green-700' },
    reveal: { className: '!bg-green-600 !text-white !border-green-700' },
    delete: { className: '!bg-green-600 !text-white !border-green-700' },
  },
  error: { className: '!bg-red-600 !text-white !border-red-700' }
};

// PAGE HEADING CONFIGURATION
export const CUSTOMER_PROFILES_HEADING = {
  label: 'Customer Profiles',
  text: 'Manage customer information and relationships',
  boldtext: '',
  labelClass: 'text-3xl font-bold text-gray-900 mb-0',
  textClass: 'text-gray-500 mt-1 text-sm sm:text-base',
  containerClass: 'flex items-center gap-2 mt-0',
};

// CARD CONFIGURATIONS (Fallback values - will be replaced by API data)
export const CUSTOMER_CARDS: CardConfig[] = [
  {
    title: 'Total Customers',
    value: '0',
    icon: 'ri-user-line',
    iconBgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    trendIcon: 'ri-arrow-up-line',
    trendText: '0% change',
    trendColor: 'text-green-600',
    layout: 'horizontal',
    iconPosition: 'right',
  },
  {
    title: 'Active Customers',
    value: '0',
    icon: 'ri-user-follow-lin',
    iconBgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    trendIcon: 'ri-arrow-up-line',
    trendText: '0% change',
    trendColor: 'text-green-600',
    layout: 'horizontal',
    iconPosition: 'right',
  },
];

// FILTER CONFIGURATION
export const CUSTOMER_PROFILE_FILTER_CONFIG: FilterConfig = {
  groups: [
    {
      id: 'main-filters',
      fields: [
        {
          name: 'searchBy',
          type: 'select',
          label: 'Search By',
          placeholder: 'All Fields',
          colSpan: 'quarter',
          options: [
            { value: 'all', label: 'All Fields' },
            { value: 'name', label: 'Name' },
            { value: 'email', label: 'Email' },
            { value: 'customer_ref', label: 'Customer ID' },
          ],
        },
        {
          name: 'searchTerm',
          type: 'search',
          label: 'Search Customers',
          placeholder: 'Search customers...',
          colSpan: 'quarter',
        },
        {
          name: 'signupMethod',
          type: 'select',
          label: 'Signed Up Via',
          placeholder: 'All Methods',
          colSpan: 'quarter',
          options: [
            { value: 'all', label: 'All Methods' },
            { value: 'email', label: 'Manual – Email' },
            { value: 'phone', label: 'Manual – Phone' },
            { value: 'google', label: 'Google OAuth' },
            { value: 'apple', label: 'Apple OAuth' },
          ],
        },
        {
          name: 'status',
          type: 'select',
          label: 'Status',
          placeholder: 'All Status',
          colSpan: 'quarter',
          options: [
            { value: 'all', label: 'All Status' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'deleted', label: 'Deleted' },
          ],
        },
      ],
      gap: 'md',
    },
    {
      id: 'loyalty-tier',
      fields: [
        {
          name: 'loyaltyTier',
          type: 'select',
          label: 'Loyalty Tier',
          placeholder: 'All Tiers',
          colSpan: 'quarter',
          options: [
            { value: 'all', label: 'All Tiers' },
            { value: 'prive', label: 'PRIVÉ' },
            { value: 'black', label: 'BLACK' },
            { value: 'elite', label: 'ELITE' },
            { value: 'signature', label: 'SIGNATURE' },
            { value: 'select', label: 'SELECT' },
          ],
        },
      ],
      gap: 'md',
      inline: true,
    },
  ],
  showSearchButton: true,
  showResetButton: true,
  searchButtonLabel: 'Search',
  resetButtonLabel: 'Reset',
  searchButtonIcon: 'ri-search-line',
  resetButtonIcon: 'ri-refresh-line',
};

export const CUSTOMER_ACTIVE_FILTERS_TEXT = {
  title: 'Active Filters:',
  fieldLabels: {
    searchTerm: 'Search',
    signupMethod: 'Signed Up Via',
    status: 'Status',
    loyaltyTier: 'Loyalty Tier',
  }
};

export const CUSTOMER_DELETE_CONFIG = {
  icon: 'ri-delete-bin-line',
  iconBgColor: 'bg-red-100',
  iconColor: 'text-red-600',
  title: 'Delete Customer',
  subtitle: 'This action cannot be undone',
  confirmButtonText: 'Delete Customer',
  confirmButtonIcon: 'ri-delete-bin-line',
  confirmButtonClass: 'px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer',
  cancelButtonClass: 'px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer',
  warningBox: {
    title: 'Warning:',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: 'ri-alert-line',
    iconColor: 'text-yellow-600',
    textColor: 'text-yellow-800',
    items: [
      'All customer data will be permanently removed',
      'Booking history will be preserved but unlinked',
      'This action cannot be reversed'
    ]
  },
  getDescription: (customerName: string) => `Are you sure you want to delete <strong>${customerName}</strong>?`
};

export const CUSTOMER_FILTER_MAPPINGS = {
  searchTerm: {
    type: 'search',
    searchBy: 'searchBy',
    searchFields: {
      all: ['name', 'email', 'id'],
      name: ['name'],
      email: ['email'],
      id: ['id'],
    }
  },
  signupMethod: {
    type: 'exact',
    customerField: 'signupMethod'
  },
  status: {
    type: 'exact',
    customerField: 'status'
  },
  loyaltyTier: {
    type: 'exact',
    customerField: 'loyaltyTier'
  }
};

export const CUSTOMER_ACTIVE_FILTERS_EXCLUDE = ['searchBy'];

export const FILTER_CONFIGS: Record<string, FilterConfig> = {
  'customer-profiles': CUSTOMER_PROFILE_FILTER_CONFIG,
};

export function getFilterConfig(moduleName: string): FilterConfig | null {
  return FILTER_CONFIGS[moduleName] || null;
}

export const PAGINATION_CONFIG = {
  pageSize: 20,
};

export const AUTH_LIMITS = {
  maxAttempts: 3,
};

export const TABLE_ACTION_LABELS = {
  edit: 'Edit',
  view: 'View',
  delete: 'Delete',
};

export const MASKING_CONFIG = {
  visiblePrefix: 4,
  maskChar: '*',
  emailDomainPrefix: 1,
  /** Placeholder values (from API/config) that indicate "no data available" for reveal fields */
  unavailablePlaceholders: ['********'] as string[],
};

export const DATE_RANGES_CONFIG = {
  yesterday: 1,
  lastWeek: 7,
  lastMonth: 31,
};

export const DEFAULT_VALUES = {
  currency: 'AED',
  points: '0 pts',
  status: 'active',
  loading: 'Loading...',
};
